import { ExternalLinkIcon } from "lucide-react";
import type { ReactNode } from "react";

import type { DesktopUpdateState } from "@cmmd-forge/contracts";

import { toastManager } from "../ui/toast";
import { Separator } from "../ui/separator";

/** The desktop shell surface the release notes link opens through. */
export type DesktopUpdateShell = { readonly openExternal: (url: string) => Promise<boolean> };

/** Where forge publishes its releases; every note links to its exact tag. */
const RELEASE_HISTORY_URL = "https://github.com/CMMD-Center/forge/releases";
const RELEASE_TAG_URL = `${RELEASE_HISTORY_URL}/tag`;

/** The release URL for an exact version; nightly suffixes are part of the tag. */
export function getDesktopUpdateReleaseUrl(version: string | null): string | null {
  const normalizedVersion = version?.trim();
  if (!normalizedVersion) return null;
  return `${RELEASE_TAG_URL}/v${encodeURIComponent(normalizedVersion)}`;
}

/**
 * Open release notes through the shell, and own the failure: a link that
 * silently does nothing reads as a dead popover, so the refusal becomes a
 * toast. Returns whether the shell opened it.
 */
export async function openDesktopUpdateReleaseNotes(
  shell: DesktopUpdateShell | undefined,
  url: string,
): Promise<boolean> {
  if (!shell) return false;
  try {
    const opened = await shell.openExternal(url);
    if (opened) return true;
  } catch {
    // Fall through to the toast below.
  }
  toastManager.add({
    type: "error",
    title: "Unable to open release notes",
    description: url,
  });
  return false;
}

function keyReleaseNoteItems(items: ReadonlyArray<string>) {
  const occurrences = new Map<string, number>();
  return items.map((item) => {
    const occurrence = occurrences.get(item) ?? 0;
    occurrences.set(item, occurrence + 1);
    return { item, key: JSON.stringify([item, occurrence]) };
  });
}

function ReleaseLink(props: {
  readonly children: string;
  readonly releaseUrl: string;
  readonly shell: DesktopUpdateShell | undefined;
}): ReactNode {
  return (
    <a
      className="mt-2 inline-flex items-center gap-1 rounded-sm text-muted-foreground text-xs leading-5 underline decoration-dotted underline-offset-4 outline-none transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
      href={props.releaseUrl}
      onClick={(event) => {
        event.preventDefault();
        void openDesktopUpdateReleaseNotes(props.shell, props.releaseUrl);
      }}
    >
      {props.children}
      <ExternalLinkIcon aria-hidden className="size-3 shrink-0" strokeWidth={2.25} />
    </a>
  );
}

/**
 * The channel release notes shown inside the update pill's popover: the
 * newest items of every release the updater gathered for this channel, each
 * linked to its exact release. Only nightly carries notes; the stable
 * channel's tooltip stays the plain text the caller passed in.
 */
export function SidebarUpdateReleaseNotes(props: {
  readonly shell: DesktopUpdateShell | undefined;
  readonly state: DesktopUpdateState;
  readonly tooltip: string;
}): ReactNode {
  const { state } = props;
  if (state.channel !== "nightly" || state.releaseNotes.length === 0) {
    return <>{props.tooltip}</>;
  }

  return (
    <div className="flex max-h-[calc(var(--available-height)-0.5rem)] min-h-0 w-fit max-w-[min(24rem,calc(100vw-2rem))] flex-col text-left">
      <div className="shrink-0 px-1">
        {state.status === "available" ? (
          <div>
            <div className="whitespace-nowrap font-medium text-sm leading-5">
              Update ready to download
            </div>
            {state.availableVersion ? (
              <div className="mt-0.5 text-muted-foreground text-xs leading-4">
                {state.availableVersion}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="font-medium text-sm leading-5">{props.tooltip}</div>
        )}
      </div>
      <div className="min-h-0 max-h-[min(28rem,calc(100vh-6rem))] overflow-y-auto px-1 pt-4 pb-1">
        {state.releaseNotes.map((releaseNote, index) => {
          const releaseUrl = getDesktopUpdateReleaseUrl(releaseNote.version);
          const omittedItemCount = Math.max(0, releaseNote.totalItems - releaseNote.items.length);
          const linkLabel =
            omittedItemCount === 0
              ? "View release on GitHub"
              : `${omittedItemCount} more ${omittedItemCount === 1 ? "change" : "changes"} on GitHub`;

          return (
            <div key={releaseNote.version}>
              {index > 0 ? <Separator className="my-3" /> : null}
              <section>
                <h3 className="text-foreground text-xs leading-4 font-semibold">
                  {index === 0 ? "What's changed" : `Changes in ${releaseNote.version}`}
                </h3>
                <ul className="mt-2 space-y-1.5 pl-4 text-popover-foreground/90 text-xs leading-5">
                  {keyReleaseNoteItems(releaseNote.items).map(({ item, key }) => (
                    <li className="list-disc break-words" key={key}>
                      {item}
                    </li>
                  ))}
                </ul>
                {releaseUrl ? (
                  <ReleaseLink releaseUrl={releaseUrl} shell={props.shell}>
                    {linkLabel}
                  </ReleaseLink>
                ) : null}
              </section>
            </div>
          );
        })}
        {state.omittedReleaseCount > 0 ? (
          <div>
            <Separator className="my-3" />
            <ReleaseLink releaseUrl={RELEASE_HISTORY_URL} shell={props.shell}>
              {`${state.omittedReleaseCount} older ${state.omittedReleaseCount === 1 ? "release" : "releases"} on GitHub`}
            </ReleaseLink>
          </div>
        ) : null}
      </div>
    </div>
  );
}
